import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { etapePrecedente, setEtapeActive } from 'store/etapes/etapesActions';
import { postAPI, reinitialiseAPI } from 'store/apiReducer';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_CREER_TITRE_DETENTION_URL,
    CONTEXT_CREER_TITRE_DETENTION,
} from 'constants/apiUrls.constants';
import { Button, Col, Row, SegmentLoader } from '@si-armes/dsfr-react';
import { FormProvider, useForm } from 'react-hook-form';
import InputFiles from 'components/Commun/Form/InputFiles';
import { getArrayFilesToSend } from 'utils/fileFunction';
import { getPath } from '../../../../../../utils/router';
import InfoChampsObligatoire from '../../../../../../components/form/infoObligatoire/InfoChampsObligatoire';
import InformationsGeneralesObligatoires from './InformationsGeneralesObligatoires';
import RoutesMesDemarches from '../../../routes';
import { isAssociation } from '../../../../../../utils/Statuts.MetierAsso.constant';
import InformationsDetenteursMetiersPersonnesMorales from './InformationsDetenteursMetiersPersonnesMorales';

const CreerTitreDetentionPersonneMoraleEtape2 = ({ type, defaultValues, isCategorieC }) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { data: informationsPersonnelles } = useSelector(
        (state) => state.monCompte.recupererInformationsPersonnelles,
    );

    const informationsMetiersAssociations =
        informationsPersonnelles?.informationsMetiersAssociations;

    // SIAR-6341 : on intègre le choix de catégorie au formulaire pour conditionner
    // l'affichage des pièces justificatives et le texte de la déclaration sur l'honneur.
    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: {
            ...defaultValues,
            isCategorieC: isCategorieC === true,
        },
    });

    const { register, handleSubmit, formState, setValue } = hookForm;

    // SIAR-6341 : synchroniser le flag catégorie si l'utilisateur revient à l'étape 1.
    useEffect(() => {
        if (isCategorieC !== undefined && isCategorieC !== null) {
            setValue('isCategorieC', isCategorieC === true);
        }
    }, [isCategorieC, setValue]);

    // Store de creation
    const {
        isLoading: isLoadingCreation,
        data: titreDetention,
        erreur: erreurCreation,
    } = useSelector((state) => state.titreDetention.creer);

    // Enregistrer les données
    const enregistrer = (data) => {
        // Envoyer les donnees pour enregistrement
        dispatch(
            postAPI(
                usagerClientReducer,
                API_CREER_TITRE_DETENTION_URL.replace(':type', type),
                data,
                CONTEXT_CREER_TITRE_DETENTION,
            ),
        );
    };

    const onSubmit = async (dataForm) => {
        const pieceJointes = await Promise.all(
            Object.entries(dataForm.pieceJointes || {})
                .filter(([, pieceJointe]) => pieceJointe?.fichiers)
                .map(async ([pieceJointeType, pieceJointe]) => {
                    const fichiers = await getArrayFilesToSend(pieceJointe.fichiers);
                    return {
                        type: pieceJointeType,
                        fichiers,
                    };
                }),
        );

        const formattedData = {
            ...dataForm,
            pieceJointes,
        };

        const hasCertificatMedicalPsychiatrique = formattedData?.pieceJointes?.find((fichier) => {
            return fichier?.type === 'certificatMedicalPsychiatrique';
        });

        if (hasCertificatMedicalPsychiatrique) {
            formattedData.jamaisEteEnTraitementPsychiatrique =
                !formattedData?.suiviTraitementPsychiatrique;
        }

        enregistrer(formattedData);
    };

    const etablissement = informationsMetiersAssociations?.etablissement;
    const adresseEtablissement = informationsMetiersAssociations?.adresseEtablissement;

    const sousStatut = informationsMetiersAssociations?.sousStatut;

    const familleDetenteur = isAssociation(sousStatut) ? 'Association' : 'Métier';

    const ligneAdresse = [
        adresseEtablissement?.numeroDeVoie,
        adresseEtablissement?.extension,
        adresseEtablissement?.libelleVoie,
        adresseEtablissement?.lieuDit,
    ]
        .filter(Boolean)
        .join(' ');

    const ligneVille = [adresseEtablissement?.codePostal, adresseEtablissement?.ville]
        .filter(Boolean)
        .join(' ');

    useEffect(() => {
        if (!isLoadingCreation && !erreurCreation && titreDetention) {
            // initialiser l'étape & le store créer titre de détention
            dispatch(setEtapeActive(1));
            dispatch(reinitialiseAPI(CONTEXT_CREER_TITRE_DETENTION));

            // Redirection
            history.push(getPath(RoutesMesDemarches));
        }
    }, [isLoadingCreation, erreurCreation, titreDetention]);

    return (
        <SegmentLoader isLoading={isLoadingCreation}>
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                        <Col col={12}>
                            <InfoChampsObligatoire />
                        </Col>
                    </Row>

                    <Row gutters className="margin-top-md">
                        <Col lg={12}>
                            <h4>Informations de la structure</h4>
                        </Col>
                    </Row>

                    <Row gutters>
                        <Col lg={12}>
                            <p className="margin-bottom-sm">
                                <strong>Nom de l'association ou raison sociale :</strong>{' '}
                                {etablissement?.raisonSociale || '-'}
                            </p>
                            <p className="margin-bottom-sm">
                                <strong>Numéro {etablissement?.typeIdEntreprise} :</strong>{' '}
                                {etablissement?.numIdEntreprise || '-'}
                            </p>
                            <p className="margin-bottom-sm">
                                <strong>Famille de détenteur :</strong> {familleDetenteur}
                            </p>

                            {!isAssociation(sousStatut) && (
                                <p className="margin-bottom-sm">
                                    <strong>Type de compte :</strong>{' '}
                                    {informationsMetiersAssociations?.typeMetier || '-'}
                                </p>
                            )}

                            {isAssociation(sousStatut) && (
                                <p className="margin-bottom-sm">
                                    <strong>Fédération de rattachement :</strong>{' '}
                                    {informationsMetiersAssociations?.typeFederation ||
                                        'Aucune affiliation'}
                                </p>
                            )}

                            <p className="margin-bottom-sm">
                                <strong>Adresse de l'établissement :</strong>{' '}
                                {[ligneAdresse, ligneVille].filter(Boolean).join(', ') || '-'}
                            </p>
                        </Col>
                    </Row>

                    <Row gutters className="margin-top-md">
                        <Col lg={12}>
                            <h4>Informations complémentaires à renseigner</h4>
                        </Col>
                    </Row>

                    <Row gutters>
                        <Col lg={12}>
                            <div className="margin-bottom-md">
                                <InputFiles
                                    label="Justificatif du lieu d'exercice"
                                    {...register('pieceJointes.justificatifLieuExercice.fichiers', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                    error={
                                        formState.errors
                                            ? formState.errors?.pieceJointes
                                                  ?.justificatifLieuExercice
                                            : null
                                    }
                                    required
                                />
                                <span className="fr-hint-text">
                                    Si ce justificatif n'a pas été fourni lors de la création de
                                    compte, vous pouvez le téléverser ici. Sinon, il est déjà
                                    présent et accessible.
                                </span>
                            </div>
                        </Col>
                    </Row>

                    <InformationsGeneralesObligatoires />

                    <InformationsDetenteursMetiersPersonnesMorales sousStatut={sousStatut} />

                    <Row gutters className="margin-top-md">
                        <Col lg={6}>
                            <Button
                                name="etapePrecedente"
                                variant="secondary"
                                onClick={() => dispatch(etapePrecedente())}
                            >
                                Retour
                            </Button>
                        </Col>
                        <Col lg={6}>
                            <div className="text-right">
                                <Button
                                    name="deposer-demande"
                                    variant="primary"
                                    type="submit"
                                    isLoading={isLoadingCreation}
                                    disabled={!formState.isValid || isLoadingCreation}
                                >
                                    Déposer la demande
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </form>
            </FormProvider>
        </SegmentLoader>
    );
};

CreerTitreDetentionPersonneMoraleEtape2.propTypes = {
    type: PropTypes.string.isRequired,
    defaultValues: PropTypes.object.isRequired,
    isCategorieC: PropTypes.bool,
};

export default CreerTitreDetentionPersonneMoraleEtape2;
